import { Add, ArrowBack } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, FormControlLabel, FormGroup, Switch } from "@mui/material";
import { ContactType, DocumentStatus } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import {
	BoundAsyncSelect,
	BoundNumberField,
	BoundTextField,
	useAsyncSelectContactQuery,
	useAsyncSelectClientProjectsQuery,
	useAsyncSelectMembersWithEmailQuery,
} from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import InvoiceItemComponent from "../components/InvoiceItem";
import { InvoiceSummary } from "../components/InvoiceSummary";
import { XmlValidationDialog, type XmlValidationDialogHandle } from "../dialogs";
import { useInvoiceValidator } from "../hooks/InvoiceValidationHooks";
import { useUpdateInvoiceHook } from "../hooks/UpdateInvoiceHook";

import type { Invoice, InvoiceItem } from "../types/CommercialDocumentTypes";
import type { RuleFactoryType } from "@repo/server-api/types";
import type { SetStateAction } from "react";
import { useCreateInvoiceHook } from "../hooks/CreateInvoiceHook";

export type InvoiceEditProps = {
	invoice: Invoice;
	setInvoice: (value: SetStateAction<Invoice>) => void;
	rulefactory?: RuleFactoryType;
};

const InvoiceEdit = (props: InvoiceEditProps) => {
	const { t } = useTranslation();

	const navigate = useNavigate();

	const { invoice, setInvoice } = props;
	const { errors, setErrors, validate } = useInvoiceValidator();

	const useBinding = <TValue,>(selector: (m: Invoice) => TValue) => {
		return useBoundDeep(invoice, setInvoice, selector, errors, setErrors);
	};

	const invoiceClientField = useBinding((i) => i.client ?? null);
	const invoiceContractorField = useBinding((i) => i.contractor ?? null);
	const invoiceProjectField = useBinding((i) => i.project ?? null);
	const invoiceClientContactPersonField = useBinding((i) => i.contactPerson ?? null);

	const searchContractorHook = useAsyncSelectContactQuery([ContactType.Contractor]);
	const searchClientHook = useAsyncSelectContactQuery([ContactType.Client]);
	const searchProjectHook = useAsyncSelectClientProjectsQuery(invoice.client?.id ?? null);
	const searchClientContactPersonHook = useAsyncSelectMembersWithEmailQuery(
		invoice.client?.id ?? null,
		[ContactType.ContactPerson],
	);

	const invoiceHeaderNoteField = useBinding((i) => i.headerNote);
	const invoiceFooterNoteField = useBinding((i) => i.footerNote);

	const paymentDeadlineDaysField = useBinding((i) => i.paymentDeadlineDays);

	const { updateInvoice, loading } = useUpdateInvoiceHook({
		onSaved: (updatedInvoice) => {
			setInvoice(updatedInvoice);
		},
	});

	const { save: createInvoice, loading: creating } = useCreateInvoiceHook({
		onSaved: (newInvoiceId: string) => {
			setInvoice((prev) => ({ ...prev, id: newInvoiceId }));
			navigate(`/invoices/edit/${encodeURIComponent(newInvoiceId)}`, { replace: true });
		},
	});

	const xmlValidationDialogRef = useRef<XmlValidationDialogHandle>(null);

	const handleValidateXml = () => {
		if (invoice.id) {
			xmlValidationDialogRef.current?.show(invoice.id);
		}
	};

	// Show the validate button only for existing invoices in draft status
	const showValidateButton = !!invoice.id && invoice.status === DocumentStatus.Draft;

	// TODO: Remove this when you find better solution
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		// Clear contact persons and project if the selected client change
		if (!loaded) {
			setLoaded(true);
			return;
		}
		invoiceProjectField.onChangeValue(null);
		searchProjectHook.resetSearch();
		invoiceClientContactPersonField.onChangeValue(null);
		searchClientContactPersonHook.resetSearch();
	}, [invoiceClientField.value?.id]);

	const handleSubmit = async () => {
		// const { isValid } = await validate(invoice);
		await validate(invoice);

		// if (!isValid || loading || creating) {
		if (loading || creating) {
			return;
		}

		if (invoice.id) updateInvoice(invoice);
		else createInvoice(invoice);
	};

	const handleCancel = () => {
		navigate(-1);
	};

	return (
		<>
			<Box
				style={{
					minHeight: "90vh",
					height: "auto",
					width: "100%",
					position: "relative",
				}}>
				<Button className="left" variant="outlined" onClick={handleCancel}>
					<ArrowBack />
				</Button>
				<div className="left purpleHeader" style={{ marginLeft: 20 }}>
					<span className="left">{invoice.id ? t("invoice-edit") : t("create-invoice")}</span>
					<FormGroup sx={{ ml: 2.5, p: 0.5 }} className="left">
						<FormControlLabel
							control={
								<Switch
									onChange={(event) => {
										setInvoice({
											...invoice,
											isProforma: event.target.checked,
										});
									}}
									checked={invoice.isProforma || false}
								/>
							}
							label={t("is-proforma")}
						/>
					</FormGroup>
				</div>
				<div className="clear" />
				<div className="w-100 paddingBottom40" style={{ marginTop: "20px" }}>
					<div className="hrTitle lightGreyFont paddingBottom40">
						<span className="left">{t("global-information").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<Box className="fullCenter">
						<BoundAsyncSelect
							label={t("clients")}
							boundedValue={invoiceClientField}
							searchHook={searchClientHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundAsyncSelect
							label={t("project")}
							boundedValue={invoiceProjectField}
							searchHook={searchProjectHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundAsyncSelect
							label={t("client-contacts")}
							boundedValue={invoiceClientContactPersonField}
							searchHook={searchClientContactPersonHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundAsyncSelect
							label={t("contractors")}
							boundedValue={invoiceContractorField}
							searchHook={searchContractorHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundNumberField
							label={t("payment-deadline-days")}
							boundedValue={paymentDeadlineDaysField}
							additionalProps={{ className: "w-20 left" }}
						/>
					</Box>
					<div className="hrTitle lightGreyFont paddingBottom40" style={{ marginTop: 20 }}>
						<span className="left">{t("invoice-header").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<Box className="fullCenter">
						<BoundTextField
							label={t("header-note")}
							boundedValue={invoiceHeaderNoteField}
							additionalProps={{
								className: "w-100 left",
								rows: 4,
								multiline: true,
								placeholder: t("invoice-header-note-placeholder"),
								focused: true,
							}}
						/>
					</Box>
					<div className="clear" />
					<InvoiceItemComponent
						invoice={invoice}
						setInvoice={setInvoice}
						errors={errors}
						setErrors={setErrors}
					/>
					<div className="clear" />
					<Button
						className="greenButton"
						variant="contained"
						data-testid="invoice-add-item-button"
						onClick={() => {
							const newItem = {
								id: crypto.randomUUID(),
								name: "",
								description: "",
								quantity: 1,
								unit: "",
								unitPrice: 0,
								discount: 0,
								totalPrice: 0,
								tax: 0.19,
								taxCode: "U19",
							};
							setInvoice((prevInvoice) => ({
								...prevInvoice,
								items: [...(prevInvoice.items || []), newItem as unknown as InvoiceItem],
							}));
							setErrors((prevErrors) => ({
								...prevErrors,
								items: "",
							}));
						}}>
						{t("article")} <Add />
					</Button>
					<InvoiceSummary items={invoice.items} />
					<div className="clear" />
					<div className="hrTitle lightGreyFont paddingBottom40" style={{ marginTop: 20 }}>
						<span className="left">{t("invoice-footer").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<Box className="fullCenter">
						<BoundTextField
							label={t("footer-note")}
							boundedValue={invoiceFooterNoteField}
							additionalProps={{ className: "w-100", rows: 4, multiline: true }}
						/>
					</Box>
				</div>
				<Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3, mb: 2, pr: "30px" }}>
					{showValidateButton && (
						<Button
							variant="outlined"
							data-testid="invoice-validate-xml-button"
							onClick={handleValidateXml}>
							{t("validate-xml")}
						</Button>
					)}
					<LoadingButton
						loading={loading || creating}
						className={loading || creating ? "" : "blueButton"}
						color="secondary"
						variant="contained"
						data-testid="invoice-save-button"
						onClick={handleSubmit}>
						{t("save")}
					</LoadingButton>
				</Box>
			</Box>
			<XmlValidationDialog ref={xmlValidationDialogRef} />
		</>
	);
};

export default InvoiceEdit;
